import React, { useState, useEffect, useCallback, memo } from "react";
import { useAuth } from "../hooks/useAuth"; // ✅ new hook
import StudentCard from "../components/StudentCard";
import { getVotePair, submitVote } from "../services/vote";
import { isApiError } from "../services/api";
import { VoteCandidate } from "../types";

import {
  Box,
  Container,
  Alert,
  CircularProgress,
  Typography,
  Button,
  alpha,
  Snackbar,
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { IS_DEV } from "../utils/misc";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */
interface VotePair {
  pair_id: string;
  left: VoteCandidate;
  right: VoteCandidate;
}

/* ------------------------------------------------------------------ */
/*  Title component (unchanged)                                        */
/* ------------------------------------------------------------------ */
const VoteTitle = memo(() => (
  <Box
    sx={{
      textAlign: "center",
      position: "sticky",
      top: 0,
      width: "100%",
      zIndex: 1,
      backgroundColor: "#f8f8f8",
      py: { xs: 0, md: 1.5 },
      display: { xs: "none", md: "block" },
    }}
  >
    <Typography
      variant="h5"
      sx={{
        fontWeight: 300,
        letterSpacing: "0.025em",
        mb: { xs: 0.5 },
        color: "#333333",
        fontSize: { xs: "0.85rem", md: "1.5rem" },
      }}
    >
      Who's more cracked?
    </Typography>
    <Typography
      variant="body1"
      sx={{
        color: "rgba(51, 51, 51, 0.7)",
        fontWeight: 300,
        letterSpacing: "0.025em",
        fontSize: { xs: "0.7rem", md: "0.8rem" },
      }}
    >
      Choose one profile to cast your vote
    </Typography>
  </Box>
));

/* ------------------------------------------------------------------ */
/*  Main Vote component                                                */
/* ------------------------------------------------------------------ */
const Vote: React.FC = () => {
  const { profile, isLoading: authLoading } = useAuth(); // ✅ context data

  const [currentPair, setCurrentPair] = useState<VotePair | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [selectedSide, setSelectedSide] = useState<"left" | "right" | null>(
    null
  );
  const [copySuccess, setCopySuccess] = useState<boolean>(false);
  const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
  const isMountedRef = React.useRef(true);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /* ----------------------------------------------------------------
   *  Fetch a pair from the API
   * ---------------------------------------------------------------- */
  const fetchVotePair = useCallback(async () => {
    if (!profile) return null;

    if (!profile.user_id && !profile.id) {
      if (IS_DEV)
        console.error(
          "[Vote.tsx] Error: No valid user ID found in profile",
          profile
        );
      throw new Error("No valid user ID found in your profile");
    }

    try {
      return await getVotePair(undefined, profile.user_id || profile.id); // Try both possible ID properties
    } catch (err) {
      if (isApiError(err)) {
        throw new Error(`API Error: ${err.message}`);
      }
      throw err instanceof Error ? err : new Error("Failed to load pair");
    }
  }, [profile]);

  /* ----------------------------------------------------------------
   *  Initial load
   * ---------------------------------------------------------------- */
  const loadInitialData = useCallback(async () => {
    if (!profile) return;

    setIsLoading(true);
    setError(null);

    try {
      const pairData = await fetchVotePair();
      setCurrentPair(pairData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load data");
      setCurrentPair(null);
    } finally {
      setIsLoading(false);
    }
  }, [profile, fetchVotePair]);

  /* ----------------------------------------------------------------
   *  Load next pair after a vote
   * ---------------------------------------------------------------- */
  const loadNewPair = useCallback(async () => {
    if (!profile) return;

    setIsLoading(true);
    setError(null);

    try {
      const pairData = await fetchVotePair();
      setCurrentPair(pairData);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load next pair");
      setCurrentPair(null);
    } finally {
      setIsLoading(false);
      setIsSubmitting(false);
    }
  }, [profile, fetchVotePair]);

  /* ----------------------------------------------------------------
   *  Run once when profile is ready
   * ---------------------------------------------------------------- */
  useEffect(() => {
    if (!authLoading && profile) loadInitialData();
  }, [authLoading, profile, loadInitialData]);

  /* ----------------------------------------------------------------
   *  Handle vote click (optimistic UI)
   * ---------------------------------------------------------------- */
  const handleVote = async (winner: VoteCandidate, loser: VoteCandidate) => {
    if (!currentPair || isSubmitting || !profile) return;

    setSelectedSide(winner.id === currentPair.left.id ? "left" : "right");
    setIsSubmitting(true);
    setError(null);

    // Always load the next pair, regardless of vote submission success
    try {
      await loadNewPair();
    } catch (err) {
      // Only show error if still mounted
      if (isMountedRef.current) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to load next pair. Please try again."
        );
      }
    }

    // Fire off the vote request in the background
    submitVote({
      pair_id: currentPair.pair_id,
      winner_id: Number(winner.id),
      loser_id: Number(loser.id),
      voter_id: profile.user_id || profile.id,
    })
      .catch((err) => {
        // Only show error if still mounted
        if (isMountedRef.current) {
          setError(
            err instanceof Error
              ? err.message
              : "An unexpected error occurred while submitting your vote."
          );
        }
      })
      .finally(() => {
        if (isMountedRef.current) {
          setSelectedSide(null);
          setIsSubmitting(false);
        }
      });
  };

  /* ----------------------------------------------------------------
   *  Combined loading flag
   * ---------------------------------------------------------------- */
  const showSpinner = authLoading || isLoading;

  /* ----------------------------------------------------------------
   *  Error handler - Copy invite link
   * ---------------------------------------------------------------- */
  const handleCopyInviteLink = () => {
    const url = window.location.origin;
    navigator.clipboard.writeText(url);
    setCopySuccess(true);
    setSnackbarOpen(true);

    // Reset after 3 seconds
    setTimeout(() => {
      if (isMountedRef.current) {
        setCopySuccess(false);
      }
    }, 3000);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  /* ----------------------------------------------------------------
   *  Render
   * ---------------------------------------------------------------- */
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        flexGrow: 1,
        bgcolor: "#f8f8f8",
        height: "100vh",
        maxHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          py: 1,
          borderBottom: "1px solid rgba(51, 51, 51, 0.1)",
        }}
      >
        <VoteTitle />
      </Box>
      <Container
        sx={{
          width: "100%",
          flexGrow: 1,
          p: 0,
          m: 0,
          minWidth: "100%",
          height: "calc(100vh - 48px)", // Account for the title bar
          maxHeight: {
            xs: "calc(100vh - 48px - 56px)", // Account for bottom mobile nav bar
            md: "calc(100vh - 48px)",
          },
          overflow: "hidden",
        }}
      >
        {error && (
          <Alert
            severity="warning"
            sx={{
              mb: { xs: 2, md: 6 },
              borderRadius: 1,
              boxShadow: `0px 2px 8px ${alpha("#333333", 0.15)}`,
              fontSize: { xs: "0.75rem", md: "0.875rem" },
              display: "flex",
              alignItems: "center",
              gap: 1,
              backgroundColor: "#fff3e0",
              color: "#e65100",
              "& .MuiAlert-icon": {
                color: "#f57c00",
              },
            }}
          >
            {error}
            {error.includes("invite more friends") && (
              <Button
                onClick={handleCopyInviteLink}
                size="small"
                startIcon={
                  copySuccess ? <CheckCircleIcon /> : <ContentCopyIcon />
                }
                sx={{
                  textTransform: "none",
                  marginLeft: 2,
                  fontSize: { xs: "0.7rem", md: "0.8rem" },
                  border: "1px solid #f57c00",
                  color: "#f57c00",
                  "&:hover": {
                    borderColor: "#e65100",
                    backgroundColor: "rgba(245, 124, 0, 0.04)",
                  },
                }}
              >
                {copySuccess ? "Copied!" : "Copy Invite Link"}
              </Button>
            )}
          </Alert>
        )}

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            gridTemplateRows: { xs: "0.1fr 80px 0.1fr", md: "1fr" },
            gap: 0,
            width: "100%",
            height: "100%",
            maxHeight: "100%",
            minWidth: "100%",
            alignItems: "stretch",
            justifyItems: "stretch",
            m: 0,
            position: "relative",
            overflow: "hidden",
          }}
        >
          {showSpinner ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: "100%",
                gridColumn: "1 / -1",
              }}
            >
              <CircularProgress
                size={24}
                thickness={4}
                sx={{ color: "#333333" }}
              />
            </Box>
          ) : currentPair && !isSubmitting ? (
            <>
              {/* First card */}
              <Box
                sx={{
                  gridRow: { xs: 1, md: "auto" },
                  gridColumn: 1,
                  height: {
                    xs: "calc(50vh - 24px - 48px - 28px)", // Half screen minus divider, title and safe area for bottom bar
                    md: "100%",
                  },
                  maxHeight: {
                    xs: "calc(50vh - 24px - 48px - 28px)",
                    md: "100%",
                  },
                  minHeight: 0,
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: `0px 4px 12px ${alpha("#333333", 0.12)}`,
                    "& > div": {
                      borderColor: "rgba(51, 51, 51, 0.6)",
                    },
                  },
                  display: "flex",
                }}
              >
                <StudentCard
                  student={currentPair.left}
                  onVote={() => handleVote(currentPair.left, currentPair.right)}
                />
              </Box>
              {/* Divider for mobile only */}
              <Box
                sx={{
                  display: { xs: "flex", md: "none" },
                  alignItems: "center",
                  justifyContent: "center",
                  width: "100%",
                  height: "100%",
                  gridRow: 2,
                  gridColumn: 1,
                  zIndex: 2,
                }}
              >
                <Box
                  sx={{
                    color: "rgba(51,51,51,0.7)",
                    width: "auto",
                    textAlign: "center",
                    fontSize: "0.75rem",
                    fontWeight: 300,
                    whiteSpace: "nowrap",
                    letterSpacing: "0.025em",
                    bgcolor: "#f8f8f8",
                    px: 2,
                    py: 0.5,
                    borderRadius: "4px",
                    border: "1px solid rgba(51, 51, 51, 0.2)",
                  }}
                >
                  click on card to vote
                </Box>
              </Box>
              {/* Second card */}
              <Box
                sx={{
                  gridRow: { xs: 3, md: "auto" },
                  gridColumn: { xs: 1, md: 2 },
                  height: {
                    xs: "calc(50vh - 24px - 48px - 28px)", // Half screen minus divider, title and safe area for bottom bar
                    md: "100%",
                  },
                  maxHeight: {
                    xs: "calc(50vh - 24px - 48px - 28px)",
                    md: "100%",
                  },
                  minHeight: 0,
                  overflow: "hidden",
                  transition: "all 0.2s ease-in-out",
                  cursor: "pointer",
                  "&:hover": {
                    boxShadow: `0px 4px 12px ${alpha("#333333", 0.12)}`,
                    "& > div": {
                      borderColor: "rgba(51, 51, 51, 0.6)",
                      backgroundColor: alpha("#ffffff", 0.6),
                    },
                  },
                  display: "flex",
                }}
              >
                <StudentCard
                  student={currentPair.right}
                  onVote={() => handleVote(currentPair.right, currentPair.left)}
                />
              </Box>
            </>
          ) : isSubmitting ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gridColumn: "1 / -1",
              }}
            >
              <CircularProgress
                size={24}
                thickness={3}
                sx={{ color: "#333333" }}
              />
              <Typography
                variant="body1"
                sx={{ color: "rgba(51,51,51,0.7)", fontWeight: 300, mt: 2 }}
              >
                Recording your vote...
              </Typography>
            </Box>
          ) : error ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                gridColumn: "1 / -1",
                p: 2,
              }}
            ></Box>
          ) : null}
        </Box>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message="Link copied to clipboard!"
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
};

export default Vote;
